// frontend/src/components/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import api from '../api';

// Initiales pour l'avatar entreprise
function getInitials(nom) {
  return nom ? nom.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : 'BTP';
}

// Couleur déterministe basée sur le nom
function getBrandColor(nom) {
  const colors = ['#e63946','#2a9d8f','#e9c46a','#f4a261','#264653','#6d6875','#457b9d','#1d3557'];
  let hash = 0;
  for (let i = 0; i < (nom || '').length; i++) hash = nom.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function KpiCard({ icon, label, value, sub, color, delay }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
      borderLeft: `4px solid ${color}`,
      animation: `fadeSlideUp 0.5s ease ${delay}s both`,
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 20px rgba(0,0,0,0.06)'; }}
    >
      <div style={{ fontSize: '28px', marginBottom: '12px' }}>{icon}</div>
      <div style={{ fontSize: '32px', fontWeight: '800', color: '#1a1a2e', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '13px', fontWeight: '600', color: color, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      {sub && <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>{sub}</div>}
    </div>
  );
}

function ChantierRow({ chantier, color }) {
  const pct = chantier.avancement || 0;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '16px',
      padding: '12px 0', borderBottom: '1px solid #f0f0f0',
    }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '8px',
        background: color + '20', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '16px', flexShrink: 0,
      }}>🏗️</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {chantier.nom}
        </div>
        <div style={{ fontSize: '12px', color: '#999' }}>
          {chantier.client_nom || '—'} · {chantier.date_fin_prevue ? chantier.date_fin_prevue.substring(0,10) : 'Date N/A'}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: pct === 100 ? '#2a9d8f' : color }}>{pct}%</div>
        <div style={{
          width: '80px', height: '6px', background: '#f0f0f0',
          borderRadius: '3px', marginTop: '4px', overflow: 'hidden',
        }}>
          <div style={{
            width: `${pct}%`, height: '100%',
            background: pct === 100 ? '#2a9d8f' : color,
            borderRadius: '3px', transition: 'width 1s ease',
          }} />
        </div>
      </div>
    </div>
  );
}

function Dashboard({ user }) {
  const [entreprise, setEntreprise]   = useState(null);
  const [stats, setStats]             = useState(null);
  const [chantiers, setChantiers]     = useState([]);
  const [clients, setClients]         = useState({ total: 0 });
  const [devis, setDevis]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [entRes, statsRes, chanRes, cliRes, devisRes] = await Promise.all([
        api.get('/users/entreprise'),
        api.get('/factures/stats'),
        api.get('/chantiers'),
        api.get('/clients?per_page=1'),
        api.get('/devis'),
      ]);
      setEntreprise(entRes.data);
      setStats(statsRes.data);
      setChantiers(chanRes.data);
      setClients(cliRes.data);
      setDevis(devisRes.data);
    } catch (err) {
      setError(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <Spinner animation="border" />
    </div>
  );

  if (error) return <Alert variant="danger">{error}</Alert>;

  const brandColor  = getBrandColor(entreprise?.nom);
  const initiales   = getInitials(entreprise?.nom);
  const chantiersEnCours = chantiers.filter(c => c.statut === 'en_cours');
  const chantiersTermines = chantiers.filter(c => c.statut === 'termine');
  const devisEnAttente = devis.filter(d => d.statut === 'envoye').length;
  const today = new Date();
  const trialEnd = entreprise?.trial_ends_at ? new Date(entreprise.trial_ends_at) : null;
  const trialDays = trialEnd ? Math.max(0, Math.ceil((trialEnd - today) / (1000 * 60 * 60 * 24))) : null;

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.7; }
        }
      `}</style>

      {/* Bannière entreprise */}
      <div style={{
        background: `linear-gradient(135deg, ${brandColor} 0%, ${brandColor}cc 100%)`,
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '32px',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        position: 'relative',
        overflow: 'hidden',
        animation: 'fadeSlideUp 0.4s ease both',
      }}>
        {/* Motif de fond */}
        <div style={{
          position: 'absolute', right: '-20px', top: '-20px',
          width: '200px', height: '200px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
        }} />
        <div style={{
          position: 'absolute', right: '60px', bottom: '-40px',
          width: '140px', height: '140px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />

        {/* Avatar entreprise */}
        <div style={{
          width: '72px', height: '72px',
          borderRadius: '18px',
          background: 'rgba(255,255,255,0.25)',
          backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '28px', fontWeight: '800',
          flexShrink: 0, border: '2px solid rgba(255,255,255,0.4)',
          letterSpacing: '-1px',
        }}>
          {initiales}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '24px', fontWeight: '800', marginBottom: '4px' }}>
            {entreprise?.nom}
          </div>
          <div style={{ opacity: 0.85, fontSize: '14px' }}>
            Bonjour, <strong>{user?.username}</strong> · {user?.role === 'admin' ? '👑 Administrateur' : '👤 ' + user?.role}
          </div>
          {entreprise?.ville && (
            <div style={{ opacity: 0.7, fontSize: '13px', marginTop: '2px' }}>
              📍 {entreprise.ville} {entreprise.siret && `· SIRET ${entreprise.siret}`}
            </div>
          )}
        </div>

        {/* Badge trial */}
        {trialDays !== null && (
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '12px',
            padding: '12px 18px',
            textAlign: 'center',
            flexShrink: 0,
          }}>
            <div style={{ fontSize: '28px', fontWeight: '800' }}>{trialDays}</div>
            <div style={{ fontSize: '11px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              jours d'essai
            </div>
            <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px' }}>
              Plan {entreprise?.plan}
            </div>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '32px',
      }}>
        <KpiCard icon="👥" label="Clients" value={clients.total || 0}
          color="#457b9d" delay={0.1} />
        <KpiCard icon="🏗️" label="Chantiers actifs" value={chantiersEnCours.length}
          sub={`${chantiersTermines.length} terminés`} color={brandColor} delay={0.15} />
        <KpiCard icon="📋" label="Devis en attente" value={devisEnAttente}
          sub={`${devis.length} total`} color="#e9c46a" delay={0.2} />
        <KpiCard icon="💰" label="Encaissé" value={`${(stats?.total_encaisse || 0).toLocaleString('fr-FR')} €`}
          sub="ce mois" color="#2a9d8f" delay={0.25} />
        <KpiCard icon="⏳" label="En attente" value={`${(stats?.total_en_attente || 0).toLocaleString('fr-FR')} €`}
          sub={`${stats?.nb_en_attente || 0} facture(s)`} color="#f4a261" delay={0.3} />
        <KpiCard icon="🚨" label="En retard" value={`${(stats?.total_en_retard || 0).toLocaleString('fr-FR')} €`}
          sub={`${stats?.nb_en_retard || 0} facture(s)`} color="#e63946" delay={0.35} />
      </div>

      {/* Chantiers en cours + Activité récente */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* Chantiers en cours */}
        <div style={{
          background: 'white', borderRadius: '16px', padding: '24px',
          boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
          animation: 'fadeSlideUp 0.5s ease 0.4s both',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h6 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '15px' }}>
              🏗️ Chantiers en cours
            </h6>
            <span style={{
              background: brandColor + '20', color: brandColor,
              borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '600',
            }}>{chantiersEnCours.length}</span>
          </div>
          {chantiersEnCours.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#ccc', padding: '24px 0', fontSize: '14px' }}>
              Aucun chantier en cours
            </div>
          ) : (
            chantiersEnCours.slice(0, 5).map(c => (
              <ChantierRow key={c.id} chantier={c} color={brandColor} />
            ))
          )}
        </div>

        {/* Devis récents */}
        <div style={{
          background: 'white', borderRadius: '16px', padding: '24px',
          boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
          animation: 'fadeSlideUp 0.5s ease 0.45s both',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h6 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '15px' }}>
              📋 Devis récents
            </h6>
            <span style={{
              background: '#e9c46a30', color: '#b5870a',
              borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '600',
            }}>{devis.length}</span>
          </div>
          {devis.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#ccc', padding: '24px 0', fontSize: '14px' }}>
              Aucun devis créé
            </div>
          ) : (
            devis.slice(0, 5).map(d => {
              const statutColors = { brouillon: '#999', envoye: '#457b9d', accepte: '#2a9d8f', refuse: '#e63946' };
              const statutLabels = { brouillon: 'Brouillon', envoye: 'Envoyé', accepte: 'Accepté', refuse: 'Refusé' };
              return (
                <div key={d.id} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '12px 0', borderBottom: '1px solid #f0f0f0',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>{d.numero_devis}</div>
                    <div style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.chantier_nom || '—'} · {d.client_nom || '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>
                      {d.montant_ttc ? `${Number(d.montant_ttc).toLocaleString('fr-FR')} €` : '—'}
                    </div>
                    <div style={{
                      fontSize: '11px', fontWeight: '600',
                      color: statutColors[d.statut] || '#999',
                      textTransform: 'uppercase', letterSpacing: '0.3px',
                    }}>
                      {statutLabels[d.statut] || d.statut}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Alerte retards */}
      {(stats?.nb_en_retard || 0) > 0 && (
        <div style={{
          marginTop: '20px',
          background: '#e6394615',
          border: '1px solid #e6394640',
          borderRadius: '12px',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'fadeSlideUp 0.5s ease 0.5s both',
        }}>
          <span style={{ fontSize: '20px', animation: 'pulse 2s infinite' }}>🚨</span>
          <div>
            <strong style={{ color: '#e63946' }}>
              {stats.nb_en_retard} facture{stats.nb_en_retard > 1 ? 's' : ''} en retard
            </strong>
            <span style={{ color: '#666', marginLeft: '8px' }}>
              — {stats.total_en_retard.toLocaleString('fr-FR')} € à encaisser
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;